import { useCallback, useState } from 'react';
import { useSupabase } from './useSupabase';
import {
  Task,
  TaskComment,
  CreateTaskDTO,
  UpdateTaskDTO,
  CreateTaskCommentDTO,
} from '@/types/task';
import { toast } from 'sonner';

export function useTasks() {
  const { supabase } = useSupabase();
  const [isLoading, setIsLoading] = useState(false);

  const fetchTasks = useCallback(
    async (companyId: string) => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('tasks')
          .select('*')
          .eq('company_id', companyId)
          .order('created_at', { ascending: false });

        if (error) throw error;

        return data as Task[];
      } catch (error) {
        console.error('Erro ao buscar tarefas:', error);
        toast.error('Erro ao carregar tarefas');
        return [];
      } finally {
        setIsLoading(false);
      }
    },
    [supabase]
  );

  const fetchTaskComments = useCallback(
    async (taskId: string) => {
      try {
        const { data, error } = await supabase
          .from('task_comments')
          .select(`
            *,
            user:user_id (
              id,
              raw_user_meta_data->>name
            )
          `)
          .eq('task_id', taskId)
          .order('created_at', { ascending: true });

        if (error) throw error;

        return data as TaskComment[];
      } catch (error) {
        console.error('Erro ao buscar comentários:', error);
        toast.error('Erro ao carregar comentários');
        return [];
      }
    },
    [supabase]
  );

  const createTask = useCallback(
    async (data: CreateTaskDTO) => {
      try {
        const { data: task, error } = await supabase
          .from('tasks')
          .insert([
            {
              company_id: data.companyId,
              title: data.title,
              description: data.description,
              due_date: data.dueDate,
              priority: data.priority,
              assigned_to: data.assignedTo,
              service_id: data.serviceId,
              client_id: data.clientId,
              created_by: (await supabase.auth.getUser()).data.user?.id,
            },
          ])
          .select()
          .single();

        if (error) throw error;

        toast.success('Tarefa criada com sucesso');
        return task as Task;
      } catch (error) {
        console.error('Erro ao criar tarefa:', error);
        toast.error('Erro ao criar tarefa');
        return null;
      }
    },
    [supabase]
  );

  const updateTask = useCallback(
    async (taskId: string, data: UpdateTaskDTO) => {
      try {
        const { data: task, error } = await supabase
          .from('tasks')
          .update({
            title: data.title,
            description: data.description,
            due_date: data.dueDate,
            priority: data.priority,
            status: data.status,
            assigned_to: data.assignedTo,
            completed_at: data.completedAt,
            updated_at: new Date(),
          })
          .eq('id', taskId)
          .select()
          .single();

        if (error) throw error;

        toast.success('Tarefa atualizada com sucesso');
        return task as Task;
      } catch (error) {
        console.error('Erro ao atualizar tarefa:', error);
        toast.error('Erro ao atualizar tarefa');
        return null;
      }
    },
    [supabase]
  );

  const createComment = useCallback(
    async (data: CreateTaskCommentDTO) => {
      try {
        const { data: comment, error } = await supabase
          .from('task_comments')
          .insert([
            {
              task_id: data.taskId,
              content: data.content,
              user_id: (await supabase.auth.getUser()).data.user?.id,
            },
          ])
          .select(
            `
            *,
            user:user_id (
              id,
              raw_user_meta_data->>name
            )
          `
          )
          .single();

        if (error) throw error;

        return comment as TaskComment;
      } catch (error) {
        console.error('Erro ao criar comentário:', error);
        toast.error('Erro ao adicionar comentário');
        return null;
      }
    },
    [supabase]
  );

  return {
    isLoading,
    fetchTasks,
    fetchTaskComments,
    createTask,
    updateTask,
    createComment,
  };
} 